import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Bell, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

export default function NotificationBell() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const esRef = useRef(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/notifications/me").then((r) => { setItems(r.data.items); setUnread(r.data.unread); }).catch((e)=>console.error("notifications load", e));

  useEffect(() => {
    load();
    // Realtime via SSE
    const token = localStorage.getItem("hh_token");
    if (!token) return;
    const es = new EventSource(`${API}/notifications/stream?token=${encodeURIComponent(token)}`);
    esRef.current = es;
    es.onmessage = (ev) => {
      try {
        const n = JSON.parse(ev.data);
        setItems((prev) => [n, ...prev].slice(0, 50));
        setUnread((u) => u + 1);
        // Optional soft chime via Web Audio API
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const o = ctx.createOscillator(); const g = ctx.createGain();
          o.type = "sine"; o.frequency.value = 880;
          g.gain.value = 0.03;
          o.connect(g); g.connect(ctx.destination);
          o.start(); o.stop(ctx.currentTime + 0.12);
          setTimeout(() => ctx.close(), 300);
        } catch (e) { console.warn("chime failed", e); }
        // Browser Notification popup — closes the pre-arrival loop so the
        // host doesn't need to keep the dashboard tab focused. Silent no-op
        // if the user never granted permission (we don't ask here — a
        // dedicated settings toggle in the future can call
        // Notification.requestPermission()). High-priority kinds show a
        // richer body and deep-link to the reception tab when clicked.
        try {
          if ("Notification" in window && Notification.permission === "granted") {
            const title = lang === "it" ? n.title_it : n.title_en;
            const body = lang === "it" ? n.body_it : n.body_en;
            const push = new Notification(title, {
              body,
              tag: n.id,
              icon: "/favicon.ico",
              silent: false,
            });
            push.onclick = () => {
              window.focus();
              if (n.link) window.location.href = n.link;
              push.close();
            };
          } else if ("Notification" in window && Notification.permission === "default") {
            // Ask once, silently on first inbound notification. Guarded by
            // localStorage so we never re-prompt after a decline.
            if (!localStorage.getItem("hh_notif_asked")) {
              localStorage.setItem("hh_notif_asked", "1");
              Notification.requestPermission().catch(() => {});
            }
          }
        } catch (e) { console.warn("browser notif failed", e); }
      } catch (e) { console.warn("SSE parse failed", e); }
    };
    es.onerror = () => { /* browser auto-reconnects */ };
    return () => { es.close(); esRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`).catch((e)=>console.error("mark read", e));
    setItems((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
    setUnread((u) => Math.max(0, u - 1));
  };
  const readAll = async () => {
    await api.post("/notifications/read-all").catch((e)=>console.error("read all", e));
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative w-9 h-9 rounded-md border border-border hover:bg-accent grid place-items-center lift"
        >
          <Bell className="w-4 h-4"/>
          {unread > 0 && (
            <span data-testid="notification-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-primary text-primary-foreground text-[10px] font-bold grid place-items-center px-1">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-popover">
        <div className="flex items-center justify-between p-3 border-b border-border">
          <div className="font-display text-lg">{lang === "it" ? "Notifiche" : "Notifications"}</div>
          {unread > 0 && (
            <button onClick={readAll} className="text-xs text-primary hover:underline" data-testid="notification-readall-btn">
              {lang === "it" ? "Segna tutte lette" : "Mark all read"}
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center p-8">{lang === "it" ? "Nessuna notifica" : "No notifications"}</div>
          ) : (
            items.map((n) => {
              // Every notification is clickable — if `link` is missing (older
              // rows persisted before we started deep-linking) we fall back to
              // the dashboard root so the click always leads somewhere useful.
              const target = n.link || "/dashboard";
              const handleClick = () => {
                if (!n.read) markRead(n.id);
                setOpen(false);
                try {
                  const url = new URL(target, window.location.origin);
                  if (url.origin === window.location.origin) navigate(url.pathname + url.search + url.hash);
                  else window.location.href = target;
                } catch { window.location.href = target; }
              };
              return (
              <div
                key={n.id}
                className={`p-3 border-b border-border last:border-0 flex gap-3 cursor-pointer hover:bg-primary/10 transition-colors ${!n.read ? "bg-primary/5" : ""}`}
                data-testid={`notif-${n.id}`}
                onClick={handleClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleClick(); } }}
              >
                <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${!n.read ? "bg-primary" : "bg-transparent"}`}/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{lang === "it" ? n.title_it : n.title_en}</div>
                  <div className="text-xs text-muted-foreground">{lang === "it" ? n.body_it : n.body_en}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString(lang === "it" ? "it-IT" : "en-US")}</div>
                </div>
                {!n.read && (
                  <button
                    onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                    className="text-muted-foreground hover:text-primary shrink-0"
                    title="Mark read"
                    data-testid={`notif-mark-read-${n.id}`}
                  >
                    <Check className="w-4 h-4"/>
                  </button>
                )}
              </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
